import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import { storeAuthData, validateAuthStorage, isAuthenticated } from "../../utils/auth";
import LoadingLogo from "../../components/LoadingLogo";
import PhoneVerificationModal from "../../components/PhoneVerificationModal";
import "./Login.css";

const AccountSetup = () => {
    const [stage, setStage] = useState(1); // 1 = login with temp password, 2 = setup account details
    const [email, setEmail] = useState("");
    const [tempPassword, setTempPassword] = useState("");
    const [name, setName] = useState("");
    const [phoneNo, setPhoneNo] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState<string>("");
    const [isLoading, setIsLoading] = useState(false);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [userId, setUserId] = useState("");
    const [userData, setUserData] = useState<any>(null);
    const navigate = useNavigate();

    // State for button disabled status
    const [isStage1ButtonDisabled, setIsStage1ButtonDisabled] = useState(true);
    const [isStage2ButtonDisabled, setIsStage2ButtonDisabled] = useState(true);

    // State for password visibility
    const [showTempPassword, setShowTempPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // State for terms acceptance
    const [termsAccepted, setTermsAccepted] = useState(false);

    // State for phone verification modal
    const [showPhoneVerification, setShowPhoneVerification] = useState(false);

    // State for account linking
    const [showLinkAccountModal, setShowLinkAccountModal] = useState(false);
    const [linkAccountPassword, setLinkAccountPassword] = useState("");
    const [isLinkingAccount, setIsLinkingAccount] = useState(false);
    const [linkAccountError, setLinkAccountError] = useState("");

    // Toggle password visibility
    const togglePasswordVisibility = (field: 'temp' | 'new' | 'confirm') => {
        if (field === 'temp') {
            setShowTempPassword(!showTempPassword);
        } else if (field === 'new') {
            setShowNewPassword(!showNewPassword);
        } else if (field === 'confirm') {
            setShowConfirmPassword(!showConfirmPassword);
        }
    };

    // Helper function to format phone number as XXX-XXX-XXXX
    const formatPhoneNumber = (value: string) => {
        // Remove all non-digit characters
        const digits = value.replace(/\D/g, '');

        // Apply formatting and limit to 10 digits
        let formatted = '';
        if (digits.length > 0) {
            formatted = digits.substring(0, 3);
        }
        if (digits.length > 3) {
            formatted += '-' + digits.substring(3, 6);
        }
        if (digits.length > 6) {
            formatted += '-' + digits.substring(6, 10);
        }

        return formatted;
    };

    // Check if already logged in
    useEffect(() => {
        if (isAuthenticated()) {
            console.log("User already authenticated, redirecting to dashboard");
            window.location.href = "/dashboard";
        }
    }, []);

    // Validate email and temporary password to enable/disable the stage 1 button
    useEffect(() => {
        const isEmailValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email.toLowerCase());
        const isTempPasswordNotEmpty = tempPassword.length > 0;
        setIsStage1ButtonDisabled(!(isEmailValid && isTempPasswordNotEmpty));
    }, [email, tempPassword]);

    // Validate stage 2 fields to enable/disable the stage 2 button
    useEffect(() => {
        const isNameNotEmpty = name.trim().length > 0;
        // Email is readonly in stage 2, assume valid after stage 1
        const isNewPasswordValid = newPassword.length >= 8;
        const doPasswordsMatch = newPassword === confirmPassword && newPassword.length > 0;
        // Validate phone number format: XXX-XXX-XXXX
        const isPhoneNoValid = /^\d{3}-\d{3}-\d{4}$/.test(phoneNo);

        // Include all validation including terms acceptance
        setIsStage2ButtonDisabled(!(isNameNotEmpty && isNewPasswordValid && doPasswordsMatch && isPhoneNoValid && termsAccepted));
    }, [name, newPassword, confirmPassword, phoneNo, termsAccepted]);


    // Handle initial login with temporary password
    const handleInitialLogin = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        // Prevent multiple submissions
        if (isLoading) return;

        // Prevent submission if button is disabled due to validation
        if (isStage1ButtonDisabled) return;

        setIsLoading(true);
        setError("");

        try {
            console.log("Verifying credentials with temporary password for:", email);

            // Call the login endpoint
            const response = await api.post('/login', {
                email: email,
                password: tempPassword
            }, { skipAuthRedirect: true } as any);

            console.log("Login response received:", response.status);

            // Extract accessToken, refreshToken, and userId from response
            const { accessToken, refreshToken, userId } = response.data;

            if (!accessToken || !refreshToken || !userId) {
                throw new Error("Invalid response: missing required authentication data");
            }

            // Store temp credentials for stage 2 use (don't log in yet)
            sessionStorage.setItem('tempAuthData', JSON.stringify({
                accessToken,
                refreshToken,
                userId,
                tempPassword
            }));

            console.log("Credentials verified, moving to account setup stage (not logged in yet)");

            // Fetch current user data using the temp token
            const userResponse = await api.get(`/users/get-user-by-id/${userId}`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                },
                skipAuthRedirect: true
            } as any);
            const userData = userResponse.data;

            // Set user data in state
            setUserId(userId);
            setName(userData.name || "");
            setPhoneNo(userData.phoneNo || "");

            // Store the full user data to preserve fields when updating
            setUserData(userData);

            // Move to stage 2 (user is NOT logged in yet)
            setStage(2);
            setIsLoading(false);

        } catch (err: any) {
            // Log the entire error object for debugging
            console.error("Login error details:", {
                error: err,
                response: err.response ? {
                    status: err.response.status,
                    data: err.response.data
                } : 'No response',
                message: err.message
            });

            console.error("Login error:", err);
            let errorMessage = "Invalid credentials";

            if (err.response) {
                if (err.response.status === 401) {
                    errorMessage = "Incorrect email or temporary password";
                } else if (err.response.status === 500) {
                    // Special handling for the specific server error about duplicate records
                    if (err.response.data && err.response.data.message &&
                        err.response.data.message.includes("Query did not return a unique result")) {
                        errorMessage = "There appears to be multiple accounts with this email address. " +
                            "Please contact your administrator for assistance.";
                    } else if (err.response.data && err.response.data.message) {
                        // Display the actual error message from the server for other 500 errors
                        errorMessage = `Server error: ${err.response.data.message}`;
                    } else {
                        errorMessage = "An unexpected server error occurred. Please try again later.";
                    }
                } else if (err.response.data) {
                    // Ensure error message is always a string
                    if (typeof err.response.data === 'string') {
                        errorMessage = err.response.data;
                    } else if (err.response.data.message && typeof err.response.data.message === 'string') {
                        errorMessage = err.response.data.message;
                    } else {
                        // If all else fails, provide a generic message
                        errorMessage = "An error occurred with the server response";
                    }
                }
            } else if (err.request) {
                errorMessage = "Server not responding. Please try again later.";
            } else if (err.message) {
                errorMessage = err.message;
            }

            setError(errorMessage);
            setIsLoading(false);
        }
    };

    // Handle account setup submission - checks phone availability first
    const handleAccountSetup = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        // Prevent multiple submissions
        if (isLoading || isRedirecting) return;

        // Prevent submission if button is disabled due to validation
        if (isStage2ButtonDisabled) return;

        // Validate passwords match
        if (newPassword !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        // Validate password length
        if (newPassword.length < 8) {
            setError("Password must be at least 8 characters long");
            return;
        }

        setIsLoading(true);
        setError("");

        try {
            // Get temp auth data for API calls
            const tempAuthDataStr = sessionStorage.getItem('tempAuthData');
            if (!tempAuthDataStr) {
                throw new Error("Session expired. Please start over.");
            }
            const tempAuthData = JSON.parse(tempAuthDataStr);

            // Check phone availability before showing OTP modal
            const normalizedPhone = phoneNo.replace(/\D/g, '');
            const checkResponse = await api.get(`/users/check-phone-availability?phoneNo=${normalizedPhone}`, {
                headers: {
                    Authorization: `Bearer ${tempAuthData.accessToken}`
                },
                skipAuthRedirect: true
            } as any);

            const availabilityData = checkResponse.data?.data;

            if (availabilityData?.status === 'PHONE_TAKEN') {
                // Phone is taken - check if it's a VehicleOwner we can link
                if (availabilityData.existingAccountRole === 'VehicleOwner') {
                    // Show link account modal instead of OTP
                    setIsLoading(false);
                    setShowLinkAccountModal(true);
                    setLinkAccountPassword("");
                    setLinkAccountError("");
                    return;
                } else {
                    // Phone taken by non-VehicleOwner (Operator, Admin, etc.)
                    setIsLoading(false);
                    setError("This phone number is already associated with another operator account. Please use a different phone number.");
                    return;
                }
            }

            // Phone is available - proceed to OTP verification
            setIsLoading(false);
            setShowPhoneVerification(true);

        } catch (err: any) {
            console.error("Error checking phone availability:", err);
            setIsLoading(false);

            if (err.response?.status === 401) {
                setError("Session expired. Please refresh and try again.");
            } else {
                // On error, still try to proceed with OTP (fail gracefully)
                setShowPhoneVerification(true);
            }
        }
    };

    // Called when phone verification is successful
    const handlePhoneVerified = async () => {
        setShowPhoneVerification(false);
        setIsLoading(true);
        setError("");

        try {
            // Get temp auth data for API calls (user is not logged in yet)
            const tempAuthDataStr = sessionStorage.getItem('tempAuthData');
            if (!tempAuthDataStr) {
                throw new Error("Temp auth data not found - cannot complete setup");
            }
            const tempAuthData = JSON.parse(tempAuthDataStr);

            // First update user info with new schema
            const updatePayload = {
                name,
                email,
                phoneNo: phoneNo.replace(/\D/g, ''),
                modifiedBy: userId,
                deleted: false
            };

            console.log("Sending update payload:", updatePayload);

            const updateResponse = await api.put(`/users/update-user-details/${userId}`, updatePayload, {
                headers: {
                    Authorization: `Bearer ${tempAuthData.accessToken}`
                },
                skipAuthRedirect: true
            } as any);
            console.log("Update response:", updateResponse.data);

            // Then change password
            const passwordPayload = {
                oldPassword: tempPassword,
                newPassword: newPassword
            };

            console.log("Sending password change payload:", { ...passwordPayload, newPassword: "***" });

            const passwordResponse = await api.post(`/users/change-password/${userId}`, passwordPayload, {
                headers: {
                    Authorization: `Bearer ${tempAuthData.accessToken}`
                },
                skipAuthRedirect: true
            } as any);
            console.log("Password change response:", passwordResponse.data);

            // Now that everything is complete including terms acceptance, log the user in
            // Store authentication data with both tokens (now user is fully logged in)
            const authData = storeAuthData(tempAuthData.userId, tempAuthData.accessToken, tempAuthData.refreshToken);

            if (!authData) {
                throw new Error("Failed to store authentication data");
            }

            // Verify the auth data was properly stored
            const isValid = validateAuthStorage();

            if (!isValid) {
                throw new Error("Auth validation failed after storage");
            }

            // Clean up temp storage
            sessionStorage.removeItem('tempAuthData');

            console.log("User successfully logged in after complete account setup");

            console.log("Account setup successful");
            setIsRedirecting(true);

            // Redirect to dashboard
            setTimeout(() => {
                console.log("Performing hard redirect to dashboard...");
                window.location.href = "/dashboard";
            }, 500);

        } catch (err: any) {
            // Log the entire error object for debugging
            console.error("Account setup error details:", {
                error: err,
                response: err.response ? {
                    status: err.response.status,
                    data: err.response.data,
                    headers: err.response.headers
                } : 'No response',
                message: err.message
            });

            console.error("Account setup error:", err);
            let errorMessage = "Failed to complete account setup";

            if (err.response) {
                if (err.response.status === 400) {
                    // Handle validation errors
                    if (err.response.data && err.response.data.message) {
                        errorMessage = err.response.data.message;
                    } else if (err.response.data && typeof err.response.data === 'string') {
                        errorMessage = err.response.data;
                    } else {
                        errorMessage = "Invalid data provided. Please check your input.";
                    }
                } else if (err.response.status === 500) {
                    // Special handling for the specific server error about duplicate records
                    if (err.response.data && err.response.data.message &&
                        err.response.data.message.includes("Query did not return a unique result")) {
                        errorMessage = "There appears to be multiple accounts with this email address. " +
                            "Please contact your administrator for assistance.";
                    } else if (err.response.data && err.response.data.message) {
                        // Display the actual error message from the server for other 500 errors
                        errorMessage = `Server error: ${err.response.data.message}`;
                    } else {
                        errorMessage = "An unexpected server error occurred. Please try again later.";
                    }
                } else if (err.response.data) {
                    // Ensure error message is always a string
                    if (typeof err.response.data === 'string') {
                        errorMessage = err.response.data;
                    } else if (err.response.data.message && typeof err.response.data.message === 'string') {
                        errorMessage = err.response.data.message;
                    } else {
                        // If all else fails, provide a generic message
                        errorMessage = "An error occurred during account setup";
                    }
                }
            } else if (err.request) {
                errorMessage = "Server not responding. Please try again later.";
            } else if (err.message) {
                errorMessage = err.message;
            }

            setError(errorMessage);
            setIsLoading(false);
        }
    };

    // Called when user cancels phone verification
    const handlePhoneVerificationCancel = () => {
        setShowPhoneVerification(false);
        // Button remains enabled, user can try again
    };

    // Called when phone verification fails because phone is taken by VehicleOwner
    const handlePhoneTakenByVehicleOwner = () => {
        setShowPhoneVerification(false);
        setShowLinkAccountModal(true);
        setLinkAccountPassword("");
        setLinkAccountError("");
    };

    // Handle account linking submission
    const handleLinkAccounts = async () => {
        if (!linkAccountPassword || isLinkingAccount) return;

        setIsLinkingAccount(true);
        setLinkAccountError("");

        try {
            const tempAuthDataStr = sessionStorage.getItem('tempAuthData');
            if (!tempAuthDataStr) {
                throw new Error("Session expired. Please start over.");
            }
            const tempAuthData = JSON.parse(tempAuthDataStr);

            // Call link-accounts endpoint - this merges VehicleOwner into Operator
            // and returns new auth tokens for the merged account
            const response = await api.post('/users/link-accounts', {
                operatorUserId: userId,
                phoneNo: phoneNo.replace(/\D/g, ''),
                password: linkAccountPassword
            }, {
                headers: {
                    Authorization: `Bearer ${tempAuthData.accessToken}`
                },
                skipAuthRedirect: true
            } as any);

            console.log("Account linking successful:", response.data);

            // Extract the merged user and new auth tokens
            const linkData = response.data?.data;
            if (!linkData?.accessToken || !linkData?.refreshToken || !linkData?.user) {
                throw new Error("Invalid response from account linking");
            }

            const mergedUser = linkData.user;
            const newAccessToken = linkData.accessToken;
            const newRefreshToken = linkData.refreshToken;

            // Update the merged user's name if the user entered a new name
            if (name && name !== mergedUser.name) {
                const updatePayload = {
                    name,
                    email: mergedUser.email,
                    phoneNo: mergedUser.phoneNo,
                    modifiedBy: mergedUser.userId,
                    deleted: false
                };

                await api.put(`/users/update-user-details/${mergedUser.userId}`, updatePayload, {
                    headers: {
                        Authorization: `Bearer ${newAccessToken}`
                    },
                    skipAuthRedirect: true
                } as any);

                console.log("Updated merged user's name");
            }

            // Set the new password on the merged account
            // The user wants to use the password they just set, not the old VehicleOwner password
            const passwordPayload = {
                oldPassword: linkAccountPassword, // Use VehicleOwner password as "old" password
                newPassword: newPassword
            };

            await api.post(`/users/change-password/${mergedUser.userId}`, passwordPayload, {
                headers: {
                    Authorization: `Bearer ${newAccessToken}`
                },
                skipAuthRedirect: true
            } as any);

            console.log("Set new password on merged account");

            // Store authentication data for the merged account
            const authData = storeAuthData(mergedUser.userId, newAccessToken, newRefreshToken);

            if (!authData) {
                throw new Error("Failed to store authentication data");
            }

            const isValid = validateAuthStorage();
            if (!isValid) {
                throw new Error("Auth validation failed after storage");
            }

            // Clean up temp storage
            sessionStorage.removeItem('tempAuthData');

            console.log("Account linking complete. User logged in as merged account:", mergedUser.userId);

            // Close modal and redirect
            setShowLinkAccountModal(false);
            setIsRedirecting(true);

            setTimeout(() => {
                window.location.href = "/dashboard";
            }, 500);

        } catch (err: any) {
            console.error("Account linking error:", err);
            let errorMessage = "Failed to link accounts";

            if (err.response) {
                if (err.response.status === 401) {
                    errorMessage = "Invalid password. Please enter the password for your app account.";
                } else if (err.response.status === 400) {
                    errorMessage = err.response.data?.message || "Cannot link these accounts.";
                } else if (err.response.status === 404) {
                    errorMessage = "No app account found with this phone number.";
                } else if (err.response.data?.message) {
                    errorMessage = err.response.data.message;
                }
            }

            setLinkAccountError(errorMessage);
            setIsLinkingAccount(false);
        }
    };

    // Cancel account linking
    const handleLinkAccountCancel = () => {
        setShowLinkAccountModal(false);
        setLinkAccountPassword("");
        setLinkAccountError("");
        // User needs to use a different phone number
        setError("Please use a different phone number, or link your existing app account.");
    };

    // Show loading/redirecting UI
    if (isRedirecting) {
        return (
            <div className="login-container">
                <div className="login-box">
                    <img src="/assets/logos/Logo_Operator.svg" alt="Parallel Operator" className="logo-operator" />
                    <div className="redirecting-message">
                        <LoadingLogo text="Account setup complete. Redirecting to dashboard..." />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="login-container">
            <div className="login-header">
                <img src="/assets/logos/Logo_Parallel.svg" alt="Parallel Logo" className="parallel-logo" />
            </div>
            <div className="login-box">
                <img src="/assets/logos/Logo_Operator_Inline.svg" alt="Parallel Operator" className="logo-operator-inline" />

                {stage === 1 && (
                    <form className="setup-stage" onSubmit={handleInitialLogin} autoComplete="off">
                        <div className="input-group">
                            <input
                                type="text"
                                id="email"
                                placeholder=" "
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="off"
                                name="email"
                                disabled={isLoading}
                            />
                            <label htmlFor="email">Email Address</label>
                        </div>
                        <div className="input-group">
                            <input
                                type={showTempPassword ? 'text' : 'password'}
                                id="tempPassword"
                                placeholder=" "
                                value={tempPassword}
                                onChange={(e) => setTempPassword(e.target.value)}
                                autoComplete="off"
                                name="tempPassword"
                                disabled={isLoading}
                            />
                            <label htmlFor="tempPassword">Temporary Password</label>
                            {tempPassword && (
                                <button type="button" onClick={() => togglePasswordVisibility('temp')} className="password-toggle-button">
                                    <img
                                        src={showTempPassword ? "/assets/HidePassword.svg" : "/assets/ShowPassword.svg"}
                                        alt={showTempPassword ? "Hide password" : "Show password"}
                                        width="24"
                                        height="24"
                                    />
                                </button>
                            )}
                        </div>
                        <button
                            type="submit"
                            className={`login-button ${isLoading ? 'loading' : ''}${isStage1ButtonDisabled ? ' login-button-disabled' : ''}`}
                            disabled={isLoading || isStage1ButtonDisabled}
                        >
                            {isLoading ? 'Verifying...' : 'Continue'}
                        </button>
                        {error && typeof error === 'string' && <p className="error">{error}</p>}
                    </form>
                )}

                {stage === 2 && (
                    <form className="setup-stage" onSubmit={handleAccountSetup}>
                        <div className="input-group">
                            <input
                                id="email"
                                type="email"
                                value={email}
                                readOnly
                                autoComplete="username"
                                name="email"
                                placeholder=" "
                                required
                            />
                            <label htmlFor="email">Email Address</label>
                        </div>
                        <div className="input-group">
                            <input
                                id="name"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                disabled={isLoading}
                                placeholder=" "
                                required
                            />
                            <label htmlFor="name">Full Name</label>
                        </div>

                        <div className="input-group">
                            <input
                                id="phoneNo"
                                type="tel"
                                value={phoneNo}
                                onChange={(e) => setPhoneNo(formatPhoneNumber(e.target.value))}
                                disabled={isLoading}
                                placeholder=" "
                            />
                            <label htmlFor="phoneNo">Phone Number</label>
                        </div>

                        <div className="input-group">
                            <input
                                id="newPassword"
                                type={showNewPassword ? 'text' : 'password'}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                disabled={isLoading}
                                autoComplete="new-password"
                                name="new-password"
                                placeholder=" "
                                required
                            />
                            <label htmlFor="newPassword">New Password</label>
                            {newPassword && (
                                <button type="button" onClick={() => togglePasswordVisibility('new')} className="password-toggle-button">
                                    <img
                                        src={showNewPassword ? "/assets/HidePassword.svg" : "/assets/ShowPassword.svg"}
                                        alt={showNewPassword ? "Hide password" : "Show password"}
                                        width="24"
                                        height="24"
                                    />
                                </button>
                            )}
                        </div>

                        <div className="input-group">
                            <input
                                id="confirmPassword"
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                disabled={isLoading}
                                autoComplete="new-password"
                                name="confirm-password"
                                placeholder=" "
                                required
                            />
                            <label htmlFor="confirmPassword">Confirm New Password</label>
                            {confirmPassword && (
                                <button type="button" onClick={() => togglePasswordVisibility('confirm')} className="password-toggle-button">
                                    <img
                                        src={showConfirmPassword ? "/assets/HidePassword.svg" : "/assets/ShowPassword.svg"}
                                        alt={showConfirmPassword ? "Hide password" : "Show password"}
                                        width="24"
                                        height="24"
                                    />
                                </button>
                            )}
                        </div>

                        <div className="password-requirements">
                            <div className={`requirement ${newPassword.length >= 8 ? 'met' : 'not-met'}`}>
                                <span className="requirement-icon">
                                    {newPassword.length >= 8 ? '✓ ' : '• '}
                                </span>
                                At least 8 characters
                            </div>
                            <div className={`requirement ${newPassword === confirmPassword && newPassword !== '' ? 'met' : 'not-met'}`}>
                                <span className="requirement-icon">
                                    {newPassword === confirmPassword && newPassword !== '' ? '✓ ' : '• '}
                                </span>
                                Passwords match
                            </div>
                        </div>

                        <div className="terms-acceptance">
                            <div className="checkbox-group">
                                <input
                                    type="checkbox"
                                    id="termsAccepted"
                                    checked={termsAccepted}
                                    onChange={(e) => setTermsAccepted(e.target.checked)}
                                    disabled={isLoading}
                                />
                                <label htmlFor="termsAccepted">
                                    I agree to the <a href="https://www.parkwithparallel.com/legal/operator/terms-of-service?redirect-from-operator=true" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="https://www.parkwithparallel.com/legal/operator/privacy-policy?redirect-from-operator=true" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                                </label>
                            </div>
                        </div>

                        <button
                            type="submit"
                            className={`login-button ${isLoading ? 'loading' : ''}${isStage2ButtonDisabled ? ' login-button-disabled' : ''}`}
                            disabled={isLoading || isStage2ButtonDisabled}
                        >
                            {isLoading ? 'Saving...' : 'Setup & Login'}
                        </button>
                        {error && typeof error === 'string' && <p className="error">{error}</p>}
                    </form>
                )}
            </div>

            {showPhoneVerification && (
                <PhoneVerificationModal
                    isOpen={showPhoneVerification}
                    phoneNumber={phoneNo}
                    onVerified={handlePhoneVerified}
                    onCancel={handlePhoneVerificationCancel}
                    onPhoneTakenByVehicleOwner={handlePhoneTakenByVehicleOwner}
                />
            )}

            {showLinkAccountModal && (
                <div className="modal-overlay">
                    <div className="modal-content link-account-modal">
                        <h2>Link Your App Account</h2>
                        <p className="link-account-description">
                            This phone number is already linked to a Parallel app account.
                            Enter your app password to link the accounts together.
                        </p>
                        <p className="link-account-info">
                            After linking, you'll be able to use the same account for both the Operator portal and the mobile app.
                        </p>

                        <div className="input-group">
                            <input
                                type="password"
                                id="linkPassword"
                                value={linkAccountPassword}
                                onChange={(e) => setLinkAccountPassword(e.target.value)}
                                placeholder=" "
                                disabled={isLinkingAccount}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && linkAccountPassword) {
                                        handleLinkAccounts();
                                    }
                                }}
                            />
                            <label htmlFor="linkPassword">App Account Password</label>
                        </div>

                        {linkAccountError && (
                            <p className="error">{linkAccountError}</p>
                        )}

                        <div className="modal-buttons">
                            <button
                                type="button"
                                className="modal-button secondary"
                                onClick={handleLinkAccountCancel}
                                disabled={isLinkingAccount}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={`modal-button primary ${isLinkingAccount ? 'loading' : ''}`}
                                onClick={handleLinkAccounts}
                                disabled={!linkAccountPassword || isLinkingAccount}
                            >
                                {isLinkingAccount ? 'Linking...' : 'Link Accounts'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountSetup;
